import React, { Component } from 'react'
import { graphql, StaticQuery } from 'gatsby'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Arrow from '@components/Arrow'
import Link from '@components/Link'
import LinkedInIcon from '@components/icons/LinkedIn'
import RichText from '@components/RichText'
import * as styles from '~/styles/layout/footer.module.scss'
import * as buttonStyles from '~/styles/components/button.module.scss'
import * as arrowStyles from '~/styles/components/arrow.module.scss'
import * as editorStyles from '~/styles/components/editor.module.scss'

export default class Footer extends Component {
  scroll (e) {
    e.preventDefault()

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  getYear () {
    const date = new Date()
    return date.getFullYear()
  }

  getParentSlug (item) {
    const parent = item.page.contentfulparent
    return parent ? `${parent.slug}/` : ''
  }

  render () {
    return (
      <StaticQuery
        query={query}
        render={({ contentfulMenu, contentfulOpeningTimes }) => (
          <footer className={styles.footer} role="contentinfo">
            <div className={classNames('grid-container')}>
              <div className={styles.inner}>
                <div className="grid-x grid-margin-x">
                  <div className="cell large-3">
                    <a href="/" className={styles.logo} aria-label="Go to homepage">
                      <img src={'/logo-white.png'} aria-hidden="true" alt="Private Jet Center Logo" />
                    </a>
                  </div>

                  <div className="cell large-9">
                    <nav className="grid-x grid-margin-x">
                      <h4 className={styles.navTitle}>
                        Opening Times
                      </h4>

                      {contentfulOpeningTimes.timesLeft && (
                        <div
                          className={classNames(
                            styles.navList,
                            'cell small-12 large-auto xlarge-4'
                          )}
                        >
                          <div
                            className={classNames(
                              styles.text,
                              editorStyles.editor
                            )}
                          >
                            <RichText
                              json={JSON.parse(
                                contentfulOpeningTimes.timesLeft.raw
                              )}
                            />
                          </div>
                        </div>
                      )}

                      {contentfulOpeningTimes.timesRight && (
                        <div
                          className={classNames(
                            styles.navList,
                            'cell small-12 large-auto xlarge-4'
                          )}
                        >
                          <div
                            className={classNames(
                              styles.text,
                              editorStyles.editor
                            )}
                          >
                            <RichText
                              json={JSON.parse(
                                contentfulOpeningTimes.timesRight.raw
                              )}
                            />
                          </div>
                        </div>
                      )}
                      <div
                        className={classNames(
                          styles.navList,
                          'cell small-12 large-auto xlarge-4'
                        )}
                      >
                        <div
                          className={classNames(
                            styles.text,
                            editorStyles.editor
                          )}
                        >
                          <p>
                            <strong>
                              Follow us
                            </strong>
                          </p>
                          <a
                            href="https://www.linkedin.com/company/private-jet-centre-london-city-airport/"
                            target="_blank"
                            aria-label="Follow us on LinkedIn"
                            rel="noreferrer"
                            className={classNames(styles.social)}
                          >
                            <LinkedInIcon />
                            {' '}
                            LinkedIn
                          </a>
                          <p>
                            <strong>
                              Find us
                            </strong>
                          </p>
                          <Link
                            href="/get-in-touch"
                            className={classNames(styles.social)}
                          >
                            Royal Docks, London E16 2PJ
                          </Link>
                        </div>
                      </div>
                    </nav>
                  </div>
                </div>

                <div className={classNames(styles.footerLogos)}>
                  <div className={classNames(styles.footerLogosAccs)}>
                    <img
                      src="/air-charter.png"
                      alt="The Air Charter Association"
                    />
                    <img src="/bbga.png" alt="BBGA" />
                  </div>
                  <Link
                    href="https://www.londoncityairport.com"
                    target="_blank"
                    className={classNames(styles.button, buttonStyles.button)}
                    aria-label="Go to main LCY site"
                    track={{
                      category: 'intent-pjc-Contact',
                      action: 'Click',
                      label: 'Go to main LCY site'
                    }}
                  >
                    Go to main LCY site
                  </Link>
                  <Arrow
                    className={classNames(
                      styles.arrow,
                      arrowStyles.primary,
                      arrowStyles.up
                    )}
                  />
                </div>

                <div
                  className={classNames(
                    'grid-x grid-margin-x',
                    styles.footSubMenu
                  )}
                >
                  <div className={classNames('cell', styles.footnote)}>
                    <p
                      className={classNames(styles.copyright, 'show-for-large')}
                    >
                      &copy;
                      {' '}
                      {`${this.getYear()} London City Airport. All Rights Reserved.`}
                    </p>

                    <ul className={styles.subMenu}>
                      <li className={styles.subMenuItem}>
                        <Link
                          href="https://www.londoncityairport.com/privacy-policy"
                          className={styles.subMenuLink}
                          target="_blank"
                        >
                          Cookie policy
                        </Link>
                      </li>
                      <li className={styles.subMenuItem}>
                        <Link
                          href="https://www.londoncityairport.com/privacy-policy"
                          className={styles.subMenuLink}
                          target="_blank"
                        >
                          Privacy
                        </Link>
                      </li>
                      <li className={styles.subMenuItem}>
                        <Link
                          href="https://www.londoncityairport.com/accessibility-statement-private-jet-centre"
                          className={styles.subMenuLink}
                          target="_blank"
                        >
                          Accessibility
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div className="cell hide-for-large">
                    <p className={styles.copyright}>
                      &copy;
                      {' '}
                      {`${this.getYear()} London City Airport. All Rights Reserved.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        )}
      />
    )
  }
}

Footer.propTypes = {
  data: PropTypes.object
}

export const query = graphql`
  query {
    contentfulMenu(name: { eq: "footer-menu" }) {
      id
      title
      items {
        id
        title
        page {
          slug
          contentfulparent {
            slug
          }
        }
      }
    }
    contentfulOpeningTimes(title: { regex: "/^(?!.*(sample))/gi" }) {
      timesLeft {
        raw
      }
      timesRight {
        raw
      }
    }
  }
`
